import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, FolderIcon, LinkIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { courseTreeQueryOptions } from "@/hooks/query/course";
import * as m from "@/paraglide/messages";
import { $deleteCourse } from "@/server/actions/course";
import type { CourseTreeNode } from "@/server/controllers/course";

import { CourseTree } from "./-components/course-tree";
import { NodeDetailPanel } from "./-components/node-detail-panel";

export const Route = createFileRoute("/learn/courses/$courseId")({
	component: RouteComponent,
	pendingComponent: PendingComponent,
	loader: ({ context, params }) => {
		// Prefetch tree data
		context.queryClient.ensureQueryData(courseTreeQueryOptions(params.courseId));
	},
});

function PendingComponent() {
	return (
		<div className="flex h-svh">
			<div className="w-72 border-r p-2 space-y-2">
				<Skeleton className="h-8" />
				<Skeleton className="h-6" />
				<Skeleton className="h-6" />
				<Skeleton className="h-6" />
			</div>
			<div className="flex-1 p-6 space-y-4">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-4 w-96" />
			</div>
		</div>
	);
}

function RouteComponent() {
	const { courseId } = Route.useParams();
	const { data } = useSuspenseQuery(courseTreeQueryOptions(courseId));
	const navigate = Route.useNavigate();

	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [showDeleteCourse, setShowDeleteCourse] = useState(false);

	const { mutate: deleteCourse } = useMutation({
		mutationKey: ["courses", "delete"],
		mutationFn: () => $deleteCourse({ data: { courseId } }),
		onSuccess: () => {
			navigate({ to: "/learn", search: { tab: "courses" } });
		},
		meta: {
			success: { message: m.course_deleted() },
		},
	});

	// Find selected node recursively in the tree
	const findNode = useCallback((nodes: CourseTreeNode[], id: string): CourseTreeNode | null => {
		for (const node of nodes) {
			if (node.id === id) return node;
			const found = findNode(node.children, id);
			if (found) return found;
		}
		return null;
	}, []);

	const selectedNode = selectedNodeId ? findNode(data.nodes, selectedNodeId) : null;

	// Stats
	const stats = useMemo(() => {
		let totalNodes = 0;
		let totalLinked = 0;
		function count(nodes: CourseTreeNode[]) {
			for (const node of nodes) {
				totalNodes++;
				if (node.versionId) totalLinked++;
				count(node.children);
			}
		}
		count(data.nodes);
		return { totalNodes, totalLinked };
	}, [data.nodes]);

	return (
		<div className="flex h-svh">
			{/* Left: Tree sidebar */}
			<div className="flex w-72 shrink-0 flex-col border-r">
				{/* Back header */}
				<div className="flex items-center gap-2 border-b px-3 py-2">
					<Button
						variant="ghost"
						size="icon-sm"
						nativeButton={false}
						render={<Link to="/learn" search={{ tab: "courses" }} />}
					>
						<ArrowLeftIcon className="size-4" />
					</Button>
					<span className="min-w-0 flex-1 truncate text-sm font-semibold">
						{data.course.title || m.course_untitled()}
					</span>
				</div>

				{/* Tree */}
				<div className="flex-1 min-h-0">
					<CourseTree
						courseId={courseId}
						nodes={data.nodes}
						selectedNodeId={selectedNodeId}
						onSelectNode={setSelectedNodeId}
					/>
				</div>
			</div>

			{/* Right: Content panel */}
			<div className="flex-1 overflow-y-auto p-6">
				<div className="mx-auto max-w-2xl">
					{selectedNode ? (
						<NodeDetailPanel node={selectedNode} />
					) : (
						/* Course overview */
						<div className="space-y-6">
							<div>
								<h1 className="text-2xl font-semibold">
									{data.course.title || m.course_untitled()}
								</h1>
								{data.course.description && (
									<p className="mt-2 text-muted-foreground">
										{data.course.description}
									</p>
								)}
							</div>

							{/* Stats */}
							<div className="flex gap-6">
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<FolderIcon className="size-4" />
									{m.course_totalNodes()}: {stats.totalNodes}
								</div>
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<LinkIcon className="size-4" />
									{m.course_totalLinked()}: {stats.totalLinked}
								</div>
							</div>

							{/* Actions */}
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowDeleteCourse(true)}
								>
									<Trash2Icon className="size-4" />
									{m.course_delete()}
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Delete course dialog */}
			<AlertDialog open={showDeleteCourse} onOpenChange={setShowDeleteCourse}>
				<AlertDialogContent size="sm">
					<AlertDialogHeader>
						<AlertDialogTitle>{m.course_deleteConfirm()}</AlertDialogTitle>
						<AlertDialogDescription>
							{m.course_deleteCourseDesc()}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{m.common_cancel()}</AlertDialogCancel>
						<AlertDialogAction variant="destructive" onClick={() => deleteCourse()}>
							{m.common_delete()}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
